// react
import { useContext } from 'react';

// types
import { Ifr } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

// context
import { ContextIfrs, ContextProject } from '../../pages/Project/Project.context';

// mui
import { Box, Grid, Paper } from '@mui/material';

// icons
import {
	ArrowCircleRightOutlined as IconArrowCircleRight,
	DeleteOutlined as IconDelete,
	ModeEditOutlined as IconModeEdit,
} from '@mui/icons-material';

// ui
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { Currency } from '../../ui/Currency/Currency';
import { IconCircle } from '../../ui/IconCircle/IconCircle';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { Time } from '../../ui/Time/Time';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';

// dialogs
import { DialogUpdateIfrStatus } from '../../dialogs/DialogUpdateIfrStatus/DialogUpdateIfrStatus';

// links
import { LinkIfr } from '../../links/LinkIfr/LinkIfr';

// theme
import { theme } from '../../../theme';

// props
export interface CardIfrProps {
	ifr: Ifr;
}

export const CardIfr = ({ ifr }: CardIfrProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogIfr = searchParams.get('dialogIfr');

	// context
	const { refetchIfrs } = useContext(ContextIfrs);
	const { project } = useContext(ContextProject);

	// handlers
	const handleCloseDialog = () => {
		setSearchParams((params) => {
			params.delete('dialog');
			params.delete('dialogIfr');
			params.delete('dialogStatus');
			return params;
		});
	};

	// menu items
	const btnMenuItems: MenuItemProps[] = [];

	// vars
	const isDraft = ifr?.status === 'DRAFT';

	btnMenuItems.push({
		children: isDraft ? t('editReport') : t('seeReport'),
		component: RouterLink,
		icon: isDraft ? <IconModeEdit /> : <IconArrowCircleRight />,
		to: `/project/${project?.idWbg}/ifr/${ifr.id}`,
	});

	btnMenuItems.push({
		children: t('deleteReport'),
		icon: <IconDelete color="error" />,
		onClick: () => {
			setSearchParams((params) => {
				params.set('dialog', 'updateIfrStatus');
				params.set('dialogIfr', String(ifr.id));
				params.set('dialogStatus', 'DELETED');
				return params;
			});
		},
	});

	return (
		<>
			<Paper>
				<Box
					sx={(theme) => {
						return {
							position: 'relative',
							padding: theme.spacing(2),
						};
					}}>
					<Grid alignItems="center" container spacing={2}>
						<Grid item lg={2.5} xs={12}>
							<Grid alignItems="center" container spacing={2}>
								<Grid item>
									<IconCircle type="ifr" />
								</Grid>
								<Grid flex={1} item>
									<TypographyCaption id={ifr.id} />
									<LinkIfr ifr={ifr} />
								</Grid>
							</Grid>
						</Grid>
						<Grid item lg={2} xs={12}>
							<RowKeyValue
								label={t('startDate')}
								value={
									<>
										<Time date={ifr.dateStart} />
										{' - '}
										<Time date={ifr.dateEnd} />
									</>
								}
							/>
						</Grid>
						<Grid item lg={1} xs={12}>
							<RowKeyValue label={t('currency')} value={<Currency currency={ifr.currency} />} />
						</Grid>
						<Grid item lg={1.5} xs={12}>
							<RowKeyValue label={t('status')} value={<ChipStatus status={ifr.status} />} />
						</Grid>
						<Grid item lg={2} xs={12}>
							<RowKeyValue
								label={t('created')}
								value={<UserAvatarModified ifr={ifr} activityTypes={['IFR_CREATED']} />}
							/>
						</Grid>
						<Grid item lg={2} xs={12}>
							<RowKeyValue
								label={t('modified')}
								value={
									<UserAvatarModified ifr={ifr} activityTypes={['IFR_UPDATED', 'IFR_CREATED']} />
								}
							/>
						</Grid>
						{btnMenuItems.length > 0 && (
							<Grid item lg={true} xs={12}>
								<Grid container justifyContent={{ lg: 'flex-end' }} spacing={1}>
									<Grid item>
										<Grid
											item
											sx={{
												position: 'absolute',
												top: theme.spacing(2),
												right: theme.spacing(2),
												[theme.breakpoints.up('lg')]: {
													position: 'relative',
													top: 'auto',
													right: 'auto',
												},
											}}>
											<BtnMenu items={btnMenuItems} />
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						)}
					</Grid>
				</Box>
			</Paper>
			<DialogUpdateIfrStatus
				isOpen={paramDialog === 'updateIfrStatus' && paramDialogIfr === String(ifr.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					refetchIfrs();
				}}
			/>
		</>
	);
};
